import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { captureTouch, hasAnalyticsConsent, trackPageView } from "utilities";

/**
 * The single place route changes are observed for measurement. Renders nothing, mounts once beside
 * `ScrollToTop`, and is the reason no page component contains analytics code.
 *
 * Two jobs, both triggered by the same event:
 *
 * - **Attribution.** Records which link brought this visitor, so a registration later in the session
 *   — or days later, after they close the tab — can be credited to it.
 * - **Page views.** GA4's automatic page view fires once per document load, which in a client-routed
 *   SPA means once per session; this reports the rest.
 *
 * Mounted outside `MainRouter`, which renders `null` until the session check resolves. That matters:
 * the landing hit is captured on a cold boot rather than being missed while the app decides whether
 * the visitor is signed in.
 */
const AnalyticsTracker = () => {
	const { pathname, search } = useLocation();

	useEffect(() => {
		// Both are non-essential and wait for the cookie banner's Accept (TTAPI-47). Without it nothing
		// is stored or sent; trackPageView is also a no-op until GA has been loaded.
		if (!hasAnalyticsConsent()) {
			return;
		}

		// Attribution first: a visitor who lands and immediately registers must already have their
		// touch stored by the time the register call reads it.
		captureTouch();
		trackPageView(pathname + search);
	}, [pathname, search]);

	return null;
};

export default AnalyticsTracker;
